import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { Trash2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import type { AdminBookNoteRowDto, AdminBookPaginatedDto } from '@masters-club/types';
import { DataTable, type BulkAction, type Column } from '@/components/tables';
import { formatDate } from '@/components/universal';
import { useConfirm } from '@/hooks/use-confirm';

const PAGE_SIZE = 20;

interface BookNotesTabProps {
  bookId: number;
}

function displayName(
  row: Pick<AdminBookNoteRowDto, 'firstName' | 'lastName' | 'username' | 'telegramId' | 'userId'>,
): string {
  const parts = [row.firstName, row.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (row.username) {
    return `@${row.username}`;
  }
  return `ID: ${row.telegramId ?? row.userId}`;
}

function formatPosition(seconds: number): string {
  if (seconds <= 0) { return '0:00'; }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function BookNotesTab({ bookId }: BookNotesTabProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { confirm, ConfirmationDialog } = useConfirm();
  const { mutate: mutateDelete } = useCustomMutation();

  const { query } = useCustom<AdminBookPaginatedDto<AdminBookNoteRowDto>>({
    url: `/admin/books/${bookId}/notes`,
    method: 'get',
    config: {
      query: {
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      },
    },
  });

  const refetch = query?.refetch;
  const payload = query?.data?.data;
  const items: AdminBookNoteRowDto[] = payload?.data ?? [];
  const total = payload?.meta?.total ?? items.length;
  const isLoading = query?.isLoading ?? false;

  const handleDelete = useCallback(async (selectedRows: AdminBookNoteRowDto[]) => {
    if (selectedRows.length === 0) { return; }
    const isSingle = selectedRows.length === 1;
    const ok = await confirm({
      title: 'Удалить заметку?',
      description: isSingle
        ? `Заметка пользователя ${displayName(selectedRows[0])} будет удалена без возможности восстановления.`
        : `${selectedRows.length} заметок будут удалены без возможности восстановления.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      variant: 'destructive',
    });
    if (!ok) { return; }

    let completed = 0;
    for (const row of selectedRows) {
      mutateDelete(
        {
          url: `/admin/books/${bookId}/notes/${row.id}`,
          method: 'delete',
          values: {},
        },
        {
          onSuccess: () => {
            completed += 1;
            if (completed === selectedRows.length) {
              notify.success(isSingle ? 'Заметка удалена' : 'Заметки удалены');
              void refetch?.();
            }
          },
          onError: (error) => {
            notify.error(error instanceof Error ? error.message : 'Не удалось удалить заметку');
          },
        },
      );
    }
  }, [bookId, confirm, mutateDelete, refetch]);

  const columns = useMemo<Column<AdminBookNoteRowDto>[]>(
    () => [
      {
        key: 'userId',
        header: 'Пользователь',
        render: (_, row) => (
          <div>
            <p className="font-medium text-sm">{displayName(row)}</p>
            <p className="text-xs text-muted-foreground">
              {row.username ? `@${row.username}` : `ID: ${row.telegramId ?? row.userId}`}
            </p>
          </div>
        ),
      },
      {
        key: 'chapterId',
        header: 'Глава',
        width: '220px',
        render: (_, row) => (
          <span className="text-xs">
            <span className="text-muted-foreground">#{row.chapterOrder + 1}</span>{' '}
            {row.chapterTitle ?? <span className="text-muted-foreground">—</span>}
          </span>
        ),
      },
      {
        key: 'positionSec',
        header: 'Позиция',
        width: '110px',
        render: (_, row) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatPosition(row.positionSec)}
          </span>
        ),
      },
      {
        key: 'content',
        header: 'Содержимое',
        render: (_, row) => (
          <p className="max-w-md text-sm line-clamp-3 whitespace-pre-wrap">{row.content}</p>
        ),
      },
      {
        key: 'createdAt',
        header: 'Дата',
        width: '150px',
        render: (_, row) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const bulkActions = useMemo<BulkAction<AdminBookNoteRowDto>[]>(
    () => [
      {
        label: 'Удалить',
        icon: <Trash2 className="h-4 w-4" />,
        variant: 'destructive',
        showOnlyWhenSelected: true,
        onClick: (selectedRows) => {
          void handleDelete(selectedRows);
        },
      },
    ],
    [handleDelete],
  );

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder="Поиск по пользователю или содержимому..."
        emptyMessage={search ? 'Ничего не найдено' : 'Нет заметок'}
        selectable
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}
